import React, { useState, useEffect } from 'react';
import { dataforseoService } from '../../lib/dataforseoService';
import { apiKeyService } from '../../lib/apiKeyService';
import { SecurityService } from '../../lib/securityService';

const DataForSEODebugger: React.FC = () => {
  const [settings, setSettings] = useState<{ login: string; api_key: string } | null>(null);
  const [rawSettings, setRawSettings] = useState<Record<string, unknown> | null>(null);
  const [testResult, setTestResult] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [domain, setDomain] = useState('smart-impact.ch');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Récupérer les paramètres bruts (chiffrés)
      const rawConfig = await apiKeyService.getApiConfig('dataforseo');
      setRawSettings(rawConfig);

      // Récupérer les paramètres via le service (déchiffrés)
      const settings = await dataforseoService.getSettings();
      setSettings(settings);
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
    }
  };

  const testApi = async () => {
    setIsLoading(true);
    try {
      // Test direct avec les identifiants bruts
      const result = await dataforseoService.testRankedKeywordsApi(domain);
      setTestResult(result);
    } catch (error) {
      console.error('Erreur lors du test API:', error);
      setTestResult({ error: String(error) });
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour tester manuellement le déchiffrement
  const testDecryption = async () => {
    if (!rawSettings?.api_key) return;
    
    try {
      const decrypted = await SecurityService.decryptApiKey(rawSettings.api_key);
      alert(`Clé déchiffrée: ${decrypted}`);
    } catch (error) {
      console.error('Erreur de déchiffrement:', error);
      alert(`Erreur de déchiffrement: ${error}`);
    }
  };

  // Fonction pour tester l'authentification avec des identifiants manuels
  const testManualAuth = async () => {
    if (!settings) return;
    
    const authString = btoa(`${settings.login}:${settings.api_key}`);
    
    try {
      const response = await fetch("https://api.dataforseo.com/v3/dataforseo_labs/google/ranked_keywords/live", {
        method: "POST",
        headers: {
          "Authorization": `Basic ${authString}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify([{
          "target": domain,
          "location_name": "Switzerland",
          "language_name": "French",
          "limit": 1
        }])
      });
      
      const result = await response.json();
      alert(`Statut: ${result.status_code} - ${result.status_message}`);
    } catch (error) {
      console.error('Erreur test manuel:', error);
      alert(`Erreur: ${error}`);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold mb-4">Débogueur DataForSEO</h2>
      
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-2">Paramètres actuels</h3>
        <div className="bg-gray-50 p-4 rounded-md">
          <p><strong>Paramètres bruts (chiffrés):</strong></p>
          <pre className="text-xs overflow-auto max-h-40 bg-gray-100 p-2 rounded">
            {JSON.stringify(rawSettings, null, 2)}
          </pre>
          
          <p className="mt-4"><strong>Paramètres déchiffrés:</strong></p>
          <pre className="text-xs overflow-auto max-h-40 bg-gray-100 p-2 rounded">
            {JSON.stringify(settings, null, 2)}
          </pre>
        </div>
      </div>
      
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-2">Outils de diagnostic</h3>
        <div className="flex flex-col space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Domaine de test
            </label>
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          
          <div className="flex space-x-4">
            <button
              onClick={testApi}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Test en cours...' : 'Tester l\'API'}
            </button>
            
            <button
              onClick={testDecryption}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Tester le déchiffrement
            </button>
            
            <button
              onClick={testManualAuth}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
            >
              Test manuel d'authentification
            </button>
          </div>
        </div>
      </div>
      
      {testResult && (
        <div className="mt-6">
          <h3 className="text-lg font-medium mb-2">Résultat du test</h3>
          <div className="bg-gray-50 p-4 rounded-md">
            <pre className="text-xs overflow-auto max-h-96 bg-gray-100 p-2 rounded">
              {JSON.stringify(testResult, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataForSEODebugger;
